import { useEffect, useState } from "react";
import { Check, Loader2, MessageSquarePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

// Enrichissement de la FAQ par Claude (demande du propriétaire, 2026-10-06) :
// à partir des questions réellement posées au chatbot, Claude rédige des Q/R
// bilingues ; l'admin coche celles à publier — elles s'ajoutent à la page /faq
// (et à son schema FAQPage pour Google) aussitôt. Réversible (retirer = ✕).
export default function FaqEnrichi() {
  const [extras, setExtras] = useState(null);        // entrées déjà publiées
  const [drafts, setDrafts] = useState([]);          // brouillons Claude
  const [choix, setChoix] = useState({});            // index brouillon → coché
  const [busy, setBusy] = useState(false);
  const [pubBusy, setPubBusy] = useState(false);

  const charger = () => api.get("/admin/faq-extra")
    .then((r) => setExtras(r.data.items || []))
    .catch(() => setExtras([]));
  useEffect(() => { charger(); }, []);

  const generer = async () => {
    setBusy(true);
    setDrafts([]);
    try {
      const { data } = await api.post("/admin/claude/faq-suggestions");
      setDrafts(data.suggestions);
      setChoix(Object.fromEntries(data.suggestions.map((_, i) => [i, true])));
      if (!data.basee_sur_questions_reelles) {
        toast.success("Brouillons générés (le chatbot est jeune — basés sur les questions typiques des visiteurs)");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const publier = async () => {
    const retenus = drafts.filter((_, i) => choix[i]);
    if (!retenus.length) { toast.error("Cochez au moins une entrée"); return; }
    setPubBusy(true);
    try {
      const items = [...(extras || []), ...retenus];
      const { data } = await api.post("/admin/faq-extra", { items });
      toast.success(`${retenus.length} entrée(s) publiée(s) — la page FAQ est à jour (${data.total} au total)`);
      setDrafts([]);
      setChoix({});
      charger();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPubBusy(false); }
  };

  const retirer = async (idx) => {
    try {
      const items = (extras || []).filter((_, i) => i !== idx);
      await api.post("/admin/faq-extra", { items });
      setExtras(items);
      toast.success("Entrée retirée de la FAQ");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-surface border border-gold/25 rounded-xl p-7 mt-8" data-testid="faq-enrichi">
      <p className="font-semibold flex items-center gap-2">
        <MessageSquarePlus className="w-4 h-4 text-gold" /> Enrichir la FAQ avec Claude
      </p>
      <p className="text-xs text-ivory/70 mt-1 mb-4">
        Claude rédige des Q/R à partir des questions posées au chatbot. Vous validez avant publication —
        les entrées retenues s'ajoutent à la page /faq (et aux résultats enrichis Google) en FR et EN.
      </p>

      {extras === null ? (
        <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</p>
      ) : (
        <>
          {extras.length > 0 && (
            <div className="mb-5" data-testid="faq-extra-liste">
              <p className="text-[10px] uppercase tracking-widest text-ivory/50 mb-2">
                Déjà publiées dans la FAQ ({extras.length})
              </p>
              <ul className="space-y-1.5">
                {extras.map((e, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs bg-ink border border-ivory/10 rounded-lg px-3 py-2"
                    data-testid={`faq-extra-${i}`}>
                    <span className="text-ivory/80 flex-1">« {e.q_fr} »</span>
                    <button onClick={() => retirer(i)} data-testid={`faq-extra-retirer-${i}`}
                      title="Retirer de la FAQ"
                      className="text-red-300/70 hover:text-red-300 transition-colors shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {drafts.length > 0 && (
            <div className="mb-5" data-testid="faq-drafts">
              <p className="text-[10px] uppercase tracking-widest text-gold mb-2">Brouillons proposés par Claude</p>
              <ul className="space-y-2.5">
                {drafts.map((s, i) => (
                  <li key={i} className="bg-ink border border-gold/20 rounded-lg px-4 py-3" data-testid={`faq-draft-${i}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={!!choix[i]} data-testid={`faq-draft-check-${i}`}
                        onChange={(e) => setChoix((c) => ({ ...c, [i]: e.target.checked }))}
                        className="mt-1 accent-[#d4af37]" />
                      <span>
                        <span className="text-sm text-ivory font-medium block">{s.q_fr}</span>
                        <span className="text-xs text-ivory/55 block mt-0.5">{s.a_fr}</span>
                        <span className="text-[11px] text-gold/70 block mt-1 italic">{s.q_en}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button onClick={generer} disabled={busy} data-testid="faq-generer"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold/10 transition-colors duration-300 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4" />}
              {drafts.length ? "Régénérer d'autres brouillons" : "Proposer des ajouts (Claude)"}
            </button>
            {drafts.length > 0 && (
              <button onClick={publier} disabled={pubBusy} data-testid="faq-publier"
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                {pubBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Publier dans la FAQ
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
